/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getQueryClient } from '@opengeoweb/api';
import { useAuthenticationContext } from '@opengeoweb/authentication';
import { WorkspaceTab } from './WorkspaceTab';
import { viewpresetQueryOptions } from '../../api/viewpreset-api/queryOptions';
import { getWorkspace } from '../../api/workspace-api/api';
import {
  useCreateWorkspacePreset,
  useDeleteWorkspacePreset,
} from '../../api/workspace-api/hooks';
import type { AppStore } from '../../store/types';
import { getSelectedWorkspaceTitle } from '../../store/workspace/selectors';
import type { WorkspacePresetForBE } from '../../store/workspace/types';
import { WorkspacePresetAction } from '../../store/workspace/types';
import { emptyWorkspaceBEData } from '../../store/workspaceList/listener';
import { workspaceListActions } from '../../store/workspaceList/reducer';
import { getWorkspaceActionDialogDetails } from '../../store/workspaceList/selectors';
import type { WorkspaceFormValues } from '../../store/workspaceList/types';
import { useWorkspaceTranslation } from '../../utils/i18n';
import WorkspacePresetsFormDialog from '../WorkspacePresetsFormDialog/WorkspacePresetsFormDialog';

interface WorkspaceTabConnectProps {
  workspaceId: string;
  appTitle?: string;
}

export const WorkspaceTabConnect: React.FC<WorkspaceTabConnectProps> = ({
  workspaceId,
  appTitle,
}: WorkspaceTabConnectProps) => {
  const dispatch = useDispatch();
  const { t } = useWorkspaceTranslation();
  const { auth } = useAuthenticationContext();

  const { mutate: deleteWorkspace } = useDeleteWorkspacePreset();
  const { mutate: createWorkspace } = useCreateWorkspacePreset();

  const title = useSelector((store: AppStore) =>
    getSelectedWorkspaceTitle(store),
  );
  const dialogDetails = useSelector((store: AppStore) =>
    getWorkspaceActionDialogDetails(store),
  );

  const closeWorkspaceActionDialog = React.useCallback(() => {
    dispatch(workspaceListActions.closeWorkspaceActionDialogOptions());
  }, [dispatch]);

  const onSubmitForm = async (
    presetAction: WorkspacePresetAction,
    presetId: string,
    data: WorkspacePresetForBE,
  ): Promise<void> => {
    switch (presetAction) {
      case WorkspacePresetAction.DELETE:
      case WorkspacePresetAction.DELETE_SYSTEM_PRESET:
        deleteWorkspace({ id: presetId });
        break;
      case WorkspacePresetAction.DUPLICATE: {
        const oldWorkspace = presetId
          ? await getWorkspace(presetId, auth!)
          : emptyWorkspaceBEData;

        const viewsToDuplicate = await Promise.all(
          (oldWorkspace.views ?? []).map(async (view) => {
            // Already in new format
            if (view.componentType && view.initialProps) {
              const { viewPresetId, ...rest } = view;
              return rest;
            }

            // Convert old format to new
            if (view.viewPresetId) {
              try {
                const preset = await getQueryClient().fetchQuery(
                  viewpresetQueryOptions.getViewPreset(
                    view.viewPresetId,
                    auth!,
                  ),
                );
                const { viewPresetId, ...rest } = view;
                const componentType =
                  view.componentType ||
                  (preset as { componentType?: string })?.componentType;
                const initialProps =
                  view.initialProps! ||
                  (preset as { initialProps?: object })?.initialProps;
                const title =
                  view.title ||
                  (preset as { title?: string })?.title ||
                  rest.mosaicNodeId ||
                  viewPresetId;
                return {
                  ...rest,
                  title,
                  componentType,
                  initialProps,
                  originalViewPresetId: preset.id ?? view.viewPresetId,
                };
              } catch (_error) {
                return view;
              }
            }

            return view;
          }),
        );

        const duplicatedWorkspace: WorkspacePresetForBE = {
          ...oldWorkspace,
          title: data.title,
          abstract: data.abstract,
          description: data.description,
          labels: data.labels ?? oldWorkspace.labels,
          scope: data.scope ?? oldWorkspace.scope,
          views: viewsToDuplicate,
        };

        createWorkspace({
          data: duplicatedWorkspace,
          action: presetAction,
        });
        break;
      }
      // TODO: Reverted change as viewpresets were no longer saved as part of the workspace SAVE (SYSTEM) preset
      // see: https://gitlab.com/opengeoweb/opengeoweb/-/issues/6197
      // case WorkspacePresetAction.SAVE:
      // case WorkspacePresetAction.SAVE_SYSTEM_PRESET:
      //   updateWorkspace({ id: presetId, data, action: presetAction });
      // break;
      default: {
        dispatch(
          workspaceListActions.submitFormWorkspaceActionDialogOptions({
            presetAction,
            presetId,
            data,
            emptyMapPresetTitle: t('workspace-mappreset-new'),
            emptyWorkspacePresetTitle: t('workspace-new'),
          }),
        );
        break;
      }
    }
  };

  const presetId = typeof dialogDetails === 'object' && dialogDetails.presetId;
  const action = typeof dialogDetails === 'object' && dialogDetails.action;
  const formValues =
    typeof dialogDetails === 'object' && dialogDetails.formValues;
  const isFetching =
    (typeof dialogDetails === 'object' && dialogDetails.isFetching) ?? false;

  const error = typeof dialogDetails === 'object' && dialogDetails.error;

  const hasSavedWorkspace =
    typeof dialogDetails === 'object' && dialogDetails.hasSaved;

  return (
    <>
      <WorkspaceTab
        activeTab
        appTitle={appTitle}
        title={title}
        workspaceId={workspaceId}
      />
      <WorkspacePresetsFormDialog
        action={action || undefined!}
        error={error || undefined!}
        formValues={(formValues as WorkspaceFormValues) ?? undefined!}
        isLoading={isFetching}
        isOpen={Boolean(dialogDetails)}
        isWorkspaceSaved={hasSavedWorkspace}
        onClose={closeWorkspaceActionDialog}
        onFormSubmit={onSubmitForm}
        presetId={presetId || undefined!}
      />
    </>
  );
};
